import { type SlideScanDetectedIssue, SlideScanGeneralQcStatus, SlideScanIssue, type shared } from '@tig-dpqa-cloud/contract-backend-common';

import { match } from 'ts-pattern';

export type SlideScanDisplayClass = 'GOOD' | 'NEEDS_REVIEW' | 'AI_FAILED' | 'BAD';

/** Standard CSS class pattern */
export type SlideScanCssClassStandard = 'needs-review' | 'qc-interrupted' | 'has-issues' | 'no-issues';

/** Union of all possible CSS class patterns */
export type SlideScanCssClass = SlideScanCssClassStandard;

export const getAggregatedStatusDisplayValue = (
  aggregatedStatus: shared.AggregatedStatusType | undefined,
): SlideScanDisplayClass => {
  return match(aggregatedStatus)
    .with('GREEN', () => 'GOOD' as const)
    .with('RED', () => 'BAD' as const)
    .with('YELLOW', () => 'NEEDS_REVIEW' as const)
    .with(undefined, () => 'NEEDS_REVIEW' as const)
    .exhaustive();
};

export const getGeneralQcStatusDisplayValue = (
  generalQcStatus: shared.SlideScanGeneralQcStatusType,
): SlideScanDisplayClass => {
  return match(generalQcStatus)
    .with('GOOD', () => 'GOOD' as const)
    .with('BAD', () => 'BAD' as const)
    .with('NOT_SET', () => 'NEEDS_REVIEW' as const)
    .exhaustive();
};

export const getDisplayClassForSlideScanStatus = (
  {
    isEditing,
    detectedIssues,
    aiFailed,
    generalQcStatus,
    aggregatedStatus,
  }: {
    isEditing: boolean;
    detectedIssues: shared.SlideScanDetectedIssue[];
    aiFailed: boolean;
    generalQcStatus: shared.SlideScanGeneralQcStatusType;
    aggregatedStatus: shared.AggregatedStatusType | undefined;
  }): SlideScanDisplayClass => {
  if (aiFailed) { return 'AI_FAILED'; }

  if (isEditing) {
    return detectedIssues.some((issue) => issue.isUserDetected) ? 'BAD' : getAggregatedStatusDisplayValue(aggregatedStatus);
  }

  return generalQcStatus === 'NOT_SET' ? getAggregatedStatusDisplayValue(aggregatedStatus) : generalQcStatus;
};

export const iconForSlideScanStatus = (status: SlideScanDisplayClass): string => {
  return match(status)
    .with('GOOD', () => 'check-circle' as const)
    .otherwise(() => 'alert-triangle' as const);
};

export const getIsOutOfFocusBadQuality = (
  generalQcStatus: shared.SlideScanGeneralQcStatusType,
  issues: SlideScanDetectedIssue[],
  aggregatedStatus: shared.AggregatedStatusType | undefined,
): boolean => {
  return getDisplayClassForSlideScanStatus({
    isEditing: false,
    detectedIssues: issues.filter((issue) => issue.issueType === SlideScanIssue.enum.OUT_OF_FOCUS_REGION),
    aiFailed: false,
    generalQcStatus,
    aggregatedStatus,
  }) !== SlideScanGeneralQcStatus.enum.GOOD;
};

// Maps SlideScanDisplayClass to CSS class names.
export const getCssClassForStatus = (
  status: SlideScanDisplayClass,
): SlideScanCssClass => {
  return match(status)
    .with('NEEDS_REVIEW', () => 'needs-review' as const)
    .with('AI_FAILED', () => 'qc-interrupted' as const)
    .with('BAD', () => 'has-issues' as const)
    .with('GOOD', () => 'no-issues' as const)
    .exhaustive();
};

export const getDisplayClassWithCssClass = (
  params: {
    isEditing: boolean;
    detectedIssues: shared.SlideScanDetectedIssue[];
    aiFailed: boolean;
    generalQcStatus: shared.SlideScanGeneralQcStatusType;
    aggregatedStatus: shared.AggregatedStatusType | undefined;
  },
): { status: SlideScanDisplayClass; cssClass: SlideScanCssClass } => {
  const status = getDisplayClassForSlideScanStatus(params);
  const cssClass = getCssClassForStatus(status);
  return { status, cssClass };
};
